import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import getConfig from 'next/config';
import {
  Box,
  Grid,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import StyledBox, {
  StyledPaymentsOptions,
} from 'views/common/components/Logical/MembershipOverlaySteps/MembershipOverlayCheckoutStep/MembershipOverlayCheckoutStep.styles';
import { useSelector, useDispatch } from 'react-redux';

import axios, { API } from 'lib/config/axios';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { setLoading, setSnackbar } from 'redux/features/ui-slice';
import { setAppData } from 'redux/features/globalData-slice';

import MoreAboutYou from 'views/common/components/UI/Payment/MoreAboutYou';
import { MembershipOverlayAside } from 'views/common/components/Logical/MembershipOverlaySteps';
import { prepareMembershipCheckoutBody } from 'views/common/components/Logical/MembershipOverlaySteps/utils';
import capitalize from 'lib/utils/capitalize';
import Dinero from 'views/rebranded-module/common/utils/Dinero';
import { StripePaymentSection } from 'views/rebranded-module/common/components/Logical/StripePaymentSection';
import { useStripePaymentElement } from 'views/common/hooks/useStripePaymentElement';
import {
  pollMembershipPaymentResult,
  buildConfirmPaymentParams,
  buildCustomerInfo,
  buildMembershipMetadata,
  runStripePreSubmitValidation,
  initiateSetupIntentForFreeCheckout,
} from 'views/common/utils/stripePaymentUtils';

const {
  publicRuntimeConfig: { envVars },
} = getConfig();

const MembershipOverlayCheckoutStep = (props) => {
  const {
    overlayState,
    membershipOverlayData,
    membershipPaymentData,
    handleOverlayStepChange,
    membershipCheckoutCallback,
    referralCode,
  } = props;
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const handleErrorResponse = useErrorsHandler();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const checkoutStepRef = useRef();
  const appState = useSelector((state) => state.globalData.appState);
  const usePaymentElement = !!membershipPaymentData?.use_payment_element;
  const finalTotalPrice = membershipPaymentData.user.is_club_upgrade_eligible
    ? overlayState.selectedMembership.monthly_subscription_price.amount
    : Dinero(overlayState.selectedMembership.monthly_subscription_price)
      .add(
        Dinero(
          membershipOverlayData.promoted_membership_info.initiation_price,
        ),
      )
      .getAmount();

  const {
    stripeData,
    setStripeData,
    stripe,
    setStripe,
    elements,
    setElements,
    isStripeSubmitting,
    setIsStripeSubmitting,
    stripeAddressValue,
    setStripeAddressValue,
    setIsStripeAddressComplete,
    stripePromise,
  } = useStripePaymentElement();

  const [creditCardsList, setCreditCardsList] = useState(
    membershipPaymentData.credit_cards || [],
  );
  const [selectedCardId, setSelectedCardId] = useState('');
  const [creditCardErrors, setCreditCardErrors] = useState({});
  const addNewCreditCard = (newCreditCard) => {
    setCreditCardsList([...creditCardsList, newCreditCard]);
  };
  const [creditCardFormData, setCreditCardFormData] = useState({
    billing_address: {
      country: {},
      region: {},
      first_name: appState.current_user.first_name
        ? capitalize(appState.current_user.first_name)
        : '',
      last_name: appState.current_user.last_name
        ? capitalize(appState.current_user.last_name)
        : '',
      email: appState.current_user.email || '',
    },
  });
  const [creditCardFormErrors, setCreditCardFormErrors] = useState({});
  const recaptchaRef = useRef();
  const [moreAboutYouData, setMoreAboutYouData] = useState({
    date_of_birth: appState.current_user.birthdate,
  });
  const [errors, setErrors] = useState();
  const handleMoreAboutYouDataInputChange = (key, value) => {
    setMoreAboutYouData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  const resetRecaptcha = () => {
    recaptchaRef.current.reset();
    handleMoreAboutYouDataInputChange('g-recaptcha-response', null);
  };

  const postCheckout = async () => {
    try {
      const checkoutResponse = await axios.post(
        API.memberships.checkoutPost.replace(
          '{id}',
          overlayState.selectedMembership.id,
        ),
        prepareMembershipCheckoutBody(
          appState,
          membershipPaymentData,
          membershipOverlayData,
          overlayState,
          {
            selectedCardId,
            creditCardFormData,
            moreAboutYouData,
          },
          referralCode,
        ),
      );
      dispatch(
        setAppData({
          ...appState,
          current_user: {
            ...appState.current_user,
            is_member: true,
            credits: checkoutResponse.data.credits,
          },
          user_membership: checkoutResponse.data.user_membership,
        }),
      );
    } catch (error) {
      resetRecaptcha();
      switch (error.response.status) {
        case 406:
          switch (error.response.data.error_code) {
            case 'ER0003':
            case 'ER0007':
              dispatch(
                setSnackbar({
                  open: true,
                  severity: 'error',
                  message: t(
                    `fe_er_common_membership_overlay:${error.response.data.error_code}`,
                  ),
                }),
              );
              break;
            case 'ER0001':
              dispatch(
                setSnackbar({
                  open: true,
                  severity: 'error',
                  message: t(
                    'fe_er_common_membership_overlay:fill_out_required_fields',
                  ),
                }),
              );
              setErrors(error.response.data);
              setCreditCardFormErrors({
                ...error.response.data.errors?.credit_card,
                base: error.response.data.errors?.base,
              });
              checkoutStepRef.current?.scrollIntoView({ behavior: 'smooth' });
              break;
            default:
              handleErrorResponse(error);
          }
          break;
        default:
          handleErrorResponse(error);
      }
      throw new Error();
    }
  };
  useEffect(() => {
    if (!usePaymentElement) return;
    if (finalTotalPrice === 0) {
      initiateSetupIntentForFreeCheckout({
        authenticityToken: appState.authenticity_token,
      })
        .then((data) => {
          setStripeData({ ...data, customerSessionClientSecret: null });
        })
        .catch((error) => {
          handleErrorResponse(error);
        });
      return;
    }
    axios
      .post(API.stripePayments.initiatePayment, {
        authenticity_token: appState.authenticity_token,
        payment_type: 'membership',
        amount: finalTotalPrice,
        currency_code:
          overlayState.selectedMembership.monthly_subscription_price
            .currency_code || 'USD',
        user_id: appState.current_user.id,
        metadata: buildMembershipMetadata({
          membershipId: overlayState.selectedMembership.id,
          userId: appState.current_user.id,
          membershipPlan: overlayState.selectedMembership.name,
          currentUser: appState.current_user,
        }),
      })
      .then((res) => {
        setStripeData({
          clientKey: res.data.client_key,
          clientSecret: res.data.payment_intent_client_secret,
          paymentIntentId: res.data.payment_intent_id,
          customerSessionClientSecret: null,
        });
      })
      .catch((error) => {
        handleErrorResponse(error);
      });
  }, [usePaymentElement]);

  useEffect(() => {
    if (!usePaymentElement || !stripeData.paymentIntentId || !finalTotalPrice) return;
    axios
      .put(API.stripePayments.updatePayment, {
        authenticity_token: appState.authenticity_token,
        payment_intent_id: stripeData.paymentIntentId,
        payment_type: 'membership',
        amount: finalTotalPrice,
        currency_code:
          overlayState.selectedMembership.monthly_subscription_price
            .currency_code || 'USD',
      })
      .catch(() => {});
  }, [finalTotalPrice, stripeData.paymentIntentId]);

  const handleStripeCheckout = async () => {
    if (!stripe || !elements) return;

    const valid = await runStripePreSubmitValidation({
      elements,
      stripeAddressValue,
      translateError: (k) => t(`fe_er_common_membership_overlay:${k}`),
      dispatch,
    });
    if (!valid) return;

    setIsStripeSubmitting(true);
    dispatch(setLoading(true));

    if (stripeData.isSetupIntent) {
      const { setupIntent, error: siError } = await stripe.confirmSetup({
        elements,
        ...buildConfirmPaymentParams({
          stripeAddressValue,
          isStripeSavedCard: false,
          currentUser: appState.current_user,
        }),
      });

      if (siError) {
        resetRecaptcha();
        setIsStripeSubmitting(false);
        dispatch(setLoading(false));
        if (siError.type !== 'validation_error') {
          dispatch(
            setSnackbar({
              open: true,
              severity: 'error',
              message: siError.message,
            }),
          );
        }
        return;
      }

      try {
        await axios.post(
          API.memberships.checkoutPost.replace(
            '{id}',
            overlayState.selectedMembership.id,
          ),
          {
            authenticity_token: appState.authenticity_token,
            setup_intent_id: setupIntent.id,
            user_id: appState.current_user.id,
            user: {
              gender: moreAboutYouData.gender,
              date_of_birth: moreAboutYouData.date_of_birth,
              phone_number: stripeAddressValue?.phone || '',
              email: appState.current_user.email || '',
              first_name: appState.current_user.first_name || '',
              last_name: appState.current_user.last_name || '',
            },
            referral_code: referralCode,
            payment_frequency: 'monthly',
            postal_code: stripeAddressValue?.address?.postal_code || null,
            'g-recaptcha-response': moreAboutYouData['g-recaptcha-response'],
          },
        );
      } catch (error) {
        resetRecaptcha();
        setIsStripeSubmitting(false);
        dispatch(setLoading(false));
        switch (error.response?.status) {
          case 406:
            switch (error.response.data?.error_code) {
              case 'ER0001':
                setErrors(error.response.data);
                checkoutStepRef.current?.scrollIntoView({ behavior: 'smooth' });
                break;
              default:
                dispatch(
                  setSnackbar({
                    open: true,
                    severity: 'error',
                    message: t(
                      `fe_er_common_membership_overlay:${
                        error.response.data?.error_code || 'ER0007'
                      }`,
                    ),
                  }),
                );
            }
            break;
          default:
            handleErrorResponse(error);
        }
        return;
      }

      dispatch(
        setAppData({
          ...appState,
          current_user: { ...appState.current_user, is_member: true },
        }),
      );
      await Promise.resolve(membershipCheckoutCallback()).catch(() => {});
      handleOverlayStepChange();
      setIsStripeSubmitting(false);
      dispatch(setLoading(false));
      return;
    }

    try {
      await axios.post(
        API.memberships.checkoutPost.replace(
          '{id}',
          overlayState.selectedMembership.id,
        ),
        {
          authenticity_token: appState.authenticity_token,
          payment_intent_id: stripeData.paymentIntentId,
          user_id: appState.current_user.id,
          user: {
            gender: moreAboutYouData.gender,
            date_of_birth: moreAboutYouData.date_of_birth,
            phone_number: stripeAddressValue?.phone || '',
            email: appState.current_user.email || '',
            first_name: appState.current_user.first_name || '',
            last_name: appState.current_user.last_name || '',
          },
          referral_code: referralCode,
          payment_frequency: 'monthly',
          postal_code: stripeAddressValue?.address?.postal_code || null,
          'g-recaptcha-response': moreAboutYouData['g-recaptcha-response'],
        },
      );
    } catch (error) {
      resetRecaptcha();
      setIsStripeSubmitting(false);
      dispatch(setLoading(false));
      switch (error.response?.status) {
        case 406:
          switch (error.response.data?.error_code) {
            case 'ER0001':
              setErrors(error.response.data);
              checkoutStepRef.current?.scrollIntoView({ behavior: 'smooth' });
              break;
            default:
              dispatch(
                setSnackbar({
                  open: true,
                  severity: 'error',
                  message: t(
                    `fe_er_common_membership_overlay:${
                      error.response.data?.error_code || 'ER0007'
                    }`,
                  ),
                }),
              );
          }
          break;
        default:
          handleErrorResponse(error);
      }
      return;
    }

    try {
      await axios.put(API.stripePayments.updatePayment, {
        authenticity_token: appState.authenticity_token,
        payment_intent_id: stripeData.paymentIntentId,
        payment_type: 'membership',
        amount: finalTotalPrice,
        currency_code:
          overlayState.selectedMembership.monthly_subscription_price
            .currency_code || 'USD',
        customer_info: buildCustomerInfo({
          stripeAddressValue,
          name: `${appState.current_user.first_name || ''} ${
            appState.current_user.last_name || ''
          }`.trim(),
          email: appState.current_user.email || '',
        }),
      });
    } catch {
      // non-blocking: proceed even if update fails
    }

    const confirmResult = await stripe.confirmPayment({
      elements,
      ...buildConfirmPaymentParams({
        stripeAddressValue,
        isStripeSavedCard: false,
        currentUser: appState.current_user,
      }),
    });

    if (confirmResult.error) {
      if (confirmResult.error.type !== 'validation_error') {
        dispatch(
          setSnackbar({
            open: true,
            severity: 'error',
            message: confirmResult.error.message,
          }),
        );
      }
      resetRecaptcha();
      setIsStripeSubmitting(false);
      dispatch(setLoading(false));
      return;
    }

    let pollSuccessData = null;
    await pollMembershipPaymentResult({
      paymentIntentId: stripeData.paymentIntentId,
      authenticityToken: appState.authenticity_token,
      onSuccess: (res) => {
        pollSuccessData = res;
      },
      onFailed: () => {
        dispatch(
          setSnackbar({
            open: true,
            severity: 'error',
            message: t('fe_er_common_membership_overlay:payment_failed_retry'),
          }),
        );
        resetRecaptcha();
        setIsStripeSubmitting(false);
        dispatch(setLoading(false));
      },
    });

    if (!pollSuccessData) return;

    dispatch(
      setAppData({
        ...appState,
        current_user: {
          ...appState.current_user,
          is_member: true,
          credits: pollSuccessData.credits,
        },
        user_membership: pollSuccessData.user_membership,
      }),
    );

    await Promise.resolve(membershipCheckoutCallback()).catch(() => {});

    handleOverlayStepChange();
    setIsStripeSubmitting(false);
    dispatch(setLoading(false));
  };

  const handleSubscribeBtnClick = async () => {
    if (usePaymentElement) {
      await handleStripeCheckout();
      return;
    }
    try {
      dispatch(setLoading(true));
      await postCheckout();
      await membershipCheckoutCallback();
      handleOverlayStepChange();
    } catch (error) {
      if (error.message) {
        handleErrorResponse(error);
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    window.gtag('event', 'membership_signup', {
      event_category: 'Membership',
      event_action: 'Pageview',
      event_label: 'Checkout',
      eventSource: window.location.pathname.includes('/extras')
        ? 'Extras Page'
        : 'Search Rentals Page',
      membershipPlan: overlayState.selectedMembership.name,
    });
  }, []);

  return (
    <StyledBox ref={checkoutStepRef}>
      <Grid container>
        <Grid item lg={5} md={12} className="MOSTAside MOSTSecPadd" p={3}>
          <MembershipOverlayAside
            overlayState={overlayState}
            membershipOverlayData={membershipOverlayData}
          />
        </Grid>

        <Grid item lg={7} md={12}>
          <Box p={3} pb={5}>
            {usePaymentElement ? (
              <StripePaymentSection
                stripePromise={stripePromise}
                clientSecret={stripeData.clientSecret}
                setStripe={setStripe}
                setElements={setElements}
                onAddressChange={(e) => {
                  setStripeAddressValue(e.value);
                  setIsStripeAddressComplete(e.complete);
                }}
                showAddressElement
                paymentElementOptions={{
                  fields: {
                    card: { billingDetails: 'never' },
                    us_bank_account: { billingDetails: 'never' },
                  },

                  wallets: {
                    link: 'never',
                    googlePay: 'never',
                    applePay: 'never',
                  },
                }}
                defaultAddressValues={stripeAddressValue}
                allowedCountries={(
                  membershipPaymentData?.allowed_countries || []
                )
                  .map((c) => (c.iso2 === 'UK' ? 'GB' : c.iso2))
                  .filter(Boolean)}
              />
            ) : (
              <StyledPaymentsOptions
                className="styledPaymentsOptions"
                paymentOptions={{
                  creditCard: {
                    creditCardsList,
                    addNewCreditCard,
                    selectedCardId,
                    setSelectedCardId,
                    creditCardErrors,
                    setCreditCardErrors,
                    showSaveBtn: false,
                    isBillingAddressNeeded: true,
                    creditCardFormInfo: {
                      creditCardFormData: {
                        ...creditCardFormData,
                        countries: membershipPaymentData.allowed_countries,
                      },
                      setCreditCardFormData,
                      creditCardFormErrors,
                      setCreditCardFormErrors,
                    },
                  },
                  showPaymentOptions: false,
                }}
              />
            )}
            <Divider className="MODivider" />
            <Box mt={3}>
              <MoreAboutYou
                data={moreAboutYouData}
                isGift={false}
                userErrors={errors && { errors: errors.errors.user }}
                handleUserInputChange={handleMoreAboutYouDataInputChange}
                usePaymentElement={usePaymentElement}
              />
            </Box>

            {envVars.RECAPTCHA_SITE_KEY && (
              <Box mt={isMobile ? 3.5 : 3}>
                <ReCAPTCHA
                  sitekey={envVars.RECAPTCHA_SITE_KEY}
                  onChange={(value) => handleMoreAboutYouDataInputChange(
                    'g-recaptcha-response',
                    value,
                  )}
                  ref={recaptchaRef}
                />
              </Box>
            )}
            <Box mt={4} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                disableElevation
                onClick={handleSubscribeBtnClick}
                disabled={
                  (usePaymentElement
                    ? isStripeSubmitting || !stripeData.clientSecret
                    : false)
                  || !moreAboutYouData.agreedToTerms
                  || !moreAboutYouData['g-recaptcha-response']
                }>
                {t('fe_er_common_membership_overlay:subscribe_now')}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </StyledBox>
  );
};

export { MembershipOverlayCheckoutStep };
